// ─── Public Status Page — one day of history ─────────────────────────
//
// Reached by clicking a cell in a 90-day bar. The bars showed that SOMETHING
// happened on a day and then offered no way to find out what: a reader had to
// scroll the incident list and match dates by eye, and for anything older than
// that list there was no route at all.
//
// Everything here is derived from the summary the page ALREADY holds — see
// lib/day.ts for why there is no new endpoint and no per-day snapshot file. That
// also means this page works off the static snapshot while the API is down,
// which is the same promise ComponentDetail makes.
//
// ── The title is careful on purpose ──
// A red cell does not imply a published incident: an operator override writes an
// interval with no incident behind it, and an incident that started before the
// window and ended inside it is not in `summary.incidents` at all. So this page
// says "the incidents that were in effect on this day" and, when it finds none,
// says exactly that — never "nothing happened".

import { useMemo } from 'react';
import type { PublicStatusComponent, PublicStatusIncident } from '@pcp/shared';
import { IncidentCard } from './IncidentCard';
import { SIcon } from '../ui/SIcon';
import { da } from '../i18n/da';
import { isPlainLeftClick } from '../router';
import { fmtDayLabel } from '../lib/format';
import { incidentsOnDay, parseDayParam, subtreeIds } from '../lib/day';

export interface DayDetailProps {
  /** YYYY-MM-DD from the route. */
  date: string;
  /** Set = the day page for one system AND its subtree; null = the whole page. */
  componentId: string | null;
  incidents: readonly PublicStatusIncident[];
  /** The component tree, for resolving `componentId` to a name and a subtree. */
  components: readonly PublicStatusComponent[];
  /** Mirrors the other detail routes' state union. */
  state: 'loading' | 'ready' | 'notfound';
  backHref: string;
  onBack: () => void;
  hrefForIncident: (ref: string) => string;
  onOpenIncident: (ref: string) => void;
}

/** Depth-first search for one node in the tree. */
function findComponent(
  nodes: readonly PublicStatusComponent[],
  id: string,
): PublicStatusComponent | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const hit = findComponent(node.children ?? [], id);
    if (hit) return hit;
  }
  return null;
}

export function DayDetail({
  date,
  componentId,
  incidents,
  components,
  state,
  backHref,
  onBack,
  hrefForIncident,
  onOpenIncident,
}: DayDetailProps) {
  const day = useMemo(() => parseDayParam(date), [date]);
  const component = useMemo(
    () => (componentId ? findComponent(components, componentId) : null),
    [components, componentId],
  );
  // The bar is rolled up, so its day page must be too — clicking a red cell on a
  // collapsed root has to show what made it red, which may be a grandchild.
  const scope = useMemo(() => (component ? subtreeIds(component) : null), [component]);
  const matches = useMemo(
    () => (day ? incidentsOnDay(incidents, scope, day) : []),
    [incidents, scope, day],
  );

  const backLink = (
    <a
      className="sp-back"
      href={backHref}
      onClick={(event) => {
        if (!isPlainLeftClick(event)) return;
        event.preventDefault();
        onBack();
      }}
    >
      <SIcon name="chevronLeft" size={16} />
      {da.back}
    </a>
  );

  // A dead end with no way out is the worst thing a public page can do to
  // somebody already looking for help — so the back link renders in every state.
  if (!day) {
    return (
      <div className="sp-detail">
        {backLink}
        <div className="sp-card sp-card-pad sp-empty" role="alert">
          <SIcon name="unknown" size={24} />
          <p>{da.dayNotFound}</p>
        </div>
      </div>
    );
  }

  return (
    <article className="sp-detail">
      {backLink}

      <header className="sp-detail-head">
        <div className="sp-detail-title-row">
          <SIcon name="clock" size={26} style={{ color: 'var(--text-tertiary)' }} />
          <h1 className="sp-detail-title">{fmtDayLabel(day)}</h1>
        </div>
        {component && (
          <div className="sp-detail-meta">
            <span className="sp-detail-meta-item">{component.name}</span>
          </div>
        )}
      </header>

      {state === 'loading' ? (
        <div className="sp-card sp-card-pad sp-loading" role="status" aria-live="polite">
          <SIcon name="spinner" size={20} className="sp-spin" />
          <span>{da.loading}</span>
        </div>
      ) : matches.length === 0 ? (
        // NOT "nothing happened" — see the header note. An override or an
        // out-of-window incident can colour a day with no card to show for it.
        <div className="sp-card sp-card-pad sp-empty" role="status">
          <SIcon name="operational" size={24} />
          <p>{component ? da.dayEmptyForComponent(component.name) : da.dayEmpty}</p>
        </div>
      ) : (
        <>
          <p className="sp-sub-lead">
            {component ? da.dayLeadForComponent(component.name) : da.dayLead}
          </p>
          {/* `.sp-incidents`, the SAME wrapper the overview list uses — it is what
              carries the gap between cards. A made-up class name here rendered the
              cards flush against each other. */}
          <div className="sp-incidents">
            {matches.map((incident) => (
              <IncidentCard
                key={incident.id}
                incident={incident}
                href={hrefForIncident(incident.refId)}
                onOpen={() => onOpenIncident(incident.refId)}
              />
            ))}
          </div>
        </>
      )}
    </article>
  );
}
